import React, { useState } from 'react';
import { 
  Globe, 
  ArrowDownCircle, 
  ArrowUpCircle, 
  AlertTriangle, 
  Phone, 
  Users, 
  Clock, 
  Menu, 
  Voicemail, 
  Building,
  Search,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { NodeLibraryItem, DialplanNodeData } from '@/types/dialplan-nodes';

const NODE_LIBRARY: NodeLibraryItem[] = [
  // Entry Points
  {
    type: 'public_entry',
    category: 'entry',
    label: 'Public Entry',
    icon: 'Globe',
    description: 'Main entry point for public context',
    defaultData: {
      label: 'Public Context',
      description: 'Convention-based routing',
      category: 'entry',
      priority: 0,
      enabled: true
    }
  },
  
  // Routing
  {
    type: 'inbound_route',
    category: 'routing',
    label: 'Inbound Route',
    icon: 'ArrowDownCircle',
    description: 'Route incoming calls',
    defaultData: {
      label: 'New Inbound Route',
      category: 'routing',
      priority: 10,
      enabled: true,
      dialplanConfig: {
        pattern: '',
        destination: ''
      }
    }
  },
  {
    type: 'outbound_route',
    category: 'routing',
    label: 'Outbound Route',
    icon: 'ArrowUpCircle',
    description: 'Route outgoing calls',
    defaultData: {
      label: 'New Outbound Route',
      category: 'routing',
      priority: 10,
      enabled: true,
      dialplanConfig: {
        pattern: '',
        prefix: ''
      }
    }
  },
  {
    type: 'emergency_route',
    category: 'routing',
    label: 'Emergency Route',
    icon: 'AlertTriangle',
    description: 'Emergency call routing',
    defaultData: {
      label: 'Emergency Route',
      category: 'routing',
      priority: 1,
      enabled: true,
      dialplanConfig: {
        pattern: '112|911|118',
        destination: 'emergency'
      }
    }
  },
  
  // Extensions
  {
    type: 'extension',
    category: 'extensions',
    label: 'Extension',
    icon: 'Phone',
    description: 'SIP extension',
    defaultData: {
      label: 'New Extension',
      category: 'extensions',
      priority: 10,
      enabled: true,
      dialplanConfig: {
        extensionNumber: '',
        callerId: ''
      }
    }
  },
  {
    type: 'extension_group',
    category: 'extensions',
    label: 'Extension Group',
    icon: 'Users',
    description: 'Ring group of extensions',
    defaultData: {
      label: 'New Ring Group',
      category: 'extensions',
      priority: 10,
      enabled: true,
      dialplanConfig: {
        destination: 'ring_all'
      }
    }
  },
  
  // Time Conditions
  {
    type: 'time_condition',
    category: 'time',
    label: 'Time Condition',
    icon: 'Clock',
    description: 'Time-based routing',
    defaultData: {
      label: 'New Time Condition',
      category: 'time',
      priority: 5,
      enabled: true,
      dialplanConfig: {
        timeRanges: [],
        weekdays: []
      }
    }
  },
  
  // IVR
  {
    type: 'ivr_menu',
    category: 'ivr',
    label: 'IVR Menu',
    icon: 'Menu',
    description: 'Interactive voice menu',
    defaultData: {
      label: 'New IVR Menu',
      category: 'ivr',
      priority: 10,
      enabled: true,
      dialplanConfig: {
        menuOptions: []
      }
    }
  },
  
  // Voicemail
  {
    type: 'voicemail_box',
    category: 'voicemail',
    label: 'Voicemail',
    icon: 'Voicemail',
    description: 'Voicemail box',
    defaultData: {
      label: 'New Voicemail',
      category: 'voicemail',
      priority: 10,
      enabled: true
    }
  },
  
  // Destinations
  {
    type: 'tenant_context',
    category: 'destinations',
    label: 'Tenant Context',
    icon: 'Building',
    description: 'Route to tenant context',
    defaultData: {
      label: 'Tenant Context',
      category: 'destinations',
      priority: 10,
      enabled: true,
      dialplanConfig: {
        context: ''
      }
    }
  }
];

const ICON_MAP: Record<string, LucideIcon> = {
  Globe,
  ArrowDownCircle,
  ArrowUpCircle,
  AlertTriangle,
  Phone,
  Users,
  Clock,
  Menu,
  Voicemail,
  Building
};

const CATEGORY_LABELS: Record<string, string> = {
  entry: 'Entry Points',
  routing: 'Routing',
  extensions: 'Extensions',
  time: 'Time Conditions',
  ivr: 'IVR',
  voicemail: 'Voicemail',
  destinations: 'Destinations'
};

interface NodeLibraryProps {
  onNodeDragStart: (event: React.DragEvent, nodeType: string, defaultData: Partial<DialplanNodeData>) => void;
}

export const NodeLibrary: React.FC<NodeLibraryProps> = ({ onNodeDragStart }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(['entry', 'routing', 'destinations'])
  );

  const toggleCategory = (category: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  const filteredNodes = NODE_LIBRARY.filter(node =>
    node.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    node.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const nodesByCategory = filteredNodes.reduce((acc, node) => {
    const bucket = acc[node.category] ?? (acc[node.category] = []);
    bucket.push(node);
    return acc;
  }, {} as Record<string, NodeLibraryItem[]>);

  return (
    <div className="w-64 bg-white border-r border-gray-200 p-4 overflow-y-auto">
      <h3 className="text-lg font-semibold mb-4">Node Library</h3>
      
      {/* Search */}
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Search nodes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8"
          />
        </div>
      </div>

      {/* Categories */}
      <div className="space-y-2">
        {Object.entries(nodesByCategory).map(([category, nodes]) => (
          <div key={category} className="border border-gray-200 rounded-lg">
            {/* Category Header */}
            <button
              onClick={() => toggleCategory(category)}
              className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
            >
              <span className="font-medium text-sm">{CATEGORY_LABELS[category]}</span>
              {expandedCategories.has(category) ? (
                <ChevronDown className="h-4 w-4 text-gray-500" />
              ) : (
                <ChevronRight className="h-4 w-4 text-gray-500" />
              )}
            </button>

            {/* Category Nodes */}
            {expandedCategories.has(category) && (
              <div className="p-2 space-y-1 bg-gray-50">
                {nodes.map((node) => {
                  const IconComponent = ICON_MAP[node.icon];
                  return (
                    <div
                      key={node.type}
                      draggable
                      onDragStart={(e) => onNodeDragStart(e, node.type, node.defaultData)}
                      className="flex items-center gap-2 p-2 bg-white border border-gray-200 rounded cursor-move hover:border-blue-400 hover:shadow-sm transition-all"
                      title={node.description}
                    >
                      {IconComponent && <IconComponent className="h-4 w-4 text-gray-600" />}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{node.label}</div>
                        <div className="text-xs text-gray-500 truncate">{node.description}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Help Text */}
      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800">
        <p className="font-medium mb-1">How to use:</p>
        <p>Drag nodes from the library onto the canvas to add them to your dialplan.</p>
      </div>
    </div>
  );
};

